import { Injectable, inject, Injector } from '@angular/core';
import { environment } from '../../../environments/environment';
import { EnvironmentService } from './environment.service';
import { UserService } from './user/user.service';
import { HostRequestsInterceptor } from '../interceptors/host-requests.interceptor';
import { isArray } from '../helpers/utils';

@Injectable({
  providedIn: 'root',
})
export class CmsContentMapperService {
  private _env = inject(EnvironmentService);
  private _hostInterceptor = inject(HostRequestsInterceptor);
  private _injector = inject(Injector);

  /**
   * For avoiding circular dependency
   *
   * @param user
   */
  get user() {
    return this._injector.get(UserService);
  }

  /**
   * Prepare cms data for using in frontend
   *
   * @param data
   * @param extraParams {paramName: 'path.to.value'}
   */
  public mapCmsData(data: Array<any>, extraParams: any = {}): Array<any> {
    const mappedList = [];

    if (data) {
      data.forEach(dataItem => {
        const template = this._getActiveTemplate(dataItem.template);

        const prepared = {
          ...dataItem,
          template,
        };

        const mapped = {
          ...this._resolveExtraParams(prepared, extraParams),
          ...this.mapCmsTemplate(template),
        };

        mappedList.push(mapped);
      });
    }

    return mappedList;
  }

  /**
   * Map template object from CMS
   *
   * @param template
   */
  public mapCmsTemplate(template: any): any {
    if (!template || !template.content) {
      return {};
    }

    const content = template.content;

    const mapped = {};

    content.forEach(field => {
      switch (field.field.type) {
        case 'GROUPLIST':
          mapped[field.field.name] = this._mapGroupList(field.value);
          break;
        case 'IMG':
          if (field.value) {
            if (field.value.slice(-1) === '/') {
              mapped[field.field.name] = '';
            } else {
              mapped[field.field.name] = this._hostInterceptor.overrideUrl(environment.assets_host + field.value);
            }
          }
          break;
        case 'SMART_INPUT':
          mapped[field.field.name] = this._mapSmartInput(field.value);
          break;
        default:
          mapped[field.field.name] = this._overrideAllUrlsForMirrorsInText(field.value);
      }
    });

    return mapped;
  }

  /**
   * Convert group list string to Javascript Object
   *
   * @param grouplist
   * @private
   */
  private _mapGroupList(grouplist) {
    let dataArray = [];
    try {
      dataArray = JSON.parse(grouplist);
      if (!isArray(dataArray)) {
        dataArray = [];
      }
    } catch (error) {
      dataArray = [];
    }
    const parsed = {};
    dataArray.forEach(item => {
      const parsedItem = {};
      item.data.forEach(dataItem => {
        parsedItem[dataItem.key] = dataItem.val;
      });
      parsed[item.name] = parsedItem;
    });
    return parsed;
  }

  /**
   * Convert smart input value to single value
   *
   * @param smartInput
   * @private
   */
  private _mapSmartInput(smartInput) {
    if (!smartInput) {
      return '';
    }

    const smartInputValue = JSON.parse(smartInput);

    const defaultValue = smartInputValue.find(part => part.key === 'default');
    const ipCountryValue = smartInputValue.find(part => part.key === this._env.dataEnv()?.country?.short?.toUpperCase());
    const accountCountryValue = !!this.user.info &&
      this.user.info.country &&
      smartInputValue.find(part => part.key === this.user.info.country.toUpperCase());

    if (accountCountryValue) {
      return accountCountryValue.value;
    } else if (ipCountryValue) {
      return ipCountryValue.value;
    } else if (defaultValue) {
      return defaultValue.value;
    } else {
      return '';
    }
  }

  /**
   * Returns active template from template list
   *
   * @param templateList
   * @private
   */
  private _getActiveTemplate(templateList: Array<any>): any {
    if (templateList.length > 1) {
      return templateList.find(tpl => tpl.enabled);
    } else {
      return templateList[0];
    }
  }

  /**
   * Returns object with provided extra params of data item
   * Uses when need to add parameters in mapped data item that on top level, not in template
   *
   * @param dataItem
   * @param params
   * @private
   */
  private _resolveExtraParams(dataItem: any, params: any): any {
    const extraParams = {};

    Object.keys(params).forEach(param => {
      extraParams[param] = this._getDeepValue(dataItem, params[param]);
    });

    return extraParams;
  }

  /**
   * Returns value from object by string path
   *
   * @param object
   * @param path
   * @private
   */
  private _getDeepValue(object: any, path: string): any {
    const paths = path.split('.');

    let value = object;

    paths.forEach(pathStep => {
      if (value[pathStep] === undefined) {
        value = undefined;
        return undefined;
      } else {
        value = value[pathStep];
      }
    });

    return value;
  }


  /**
   * Override all url in content template
   *
   * @param value
   * @private
   */

  private _overrideAllUrlsForMirrorsInText(value: string) {
    const host = this._hostInterceptor.getDomainName();
    const regExp = new RegExp(
      '(https?:\\/\\/[\\w.-]*?)21bit([\\w.-]*\\.[a-z]{2,})',
      'gi',
    );
    const hostname = host.split('.')[1];

    return value && !host.includes('localhost') ? value.replace(regExp, hostname) : value;
  }
}
